import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

type Stage = 'customer' | 'contract_sent' | 'pilot' | 'proposal' | 'demo' | 'qualified' | 'no_deal' | 'lost';
type Health = 'active' | 'slowing' | 'dormant';

interface MonthStat { projects: number; uploads: number; files: number }
interface Credits {
  purchased: number;
  consumed: number;
  adjusted_manual: number;
  consumed_by_staff: number;
  pilot_used: number;
  contract_used: number;
  reset_removed: number;
  resets: Array<{ date: string | null; removed: number }>;
}
interface UsageCheck {
  pdfs_uploaded: number | null;
  credits_upload: number;
  notes: string[];
  issues: string[];
  ok: boolean;
}
interface Activity {
  sessions: number;
  active_minutes: number;
  sessions_total: number;
  active_minutes_total: number;
  last_seen: string | null;
  never_seen?: boolean;
}
interface AppUse {
  last_seen: string | null;
  last_action: string | null;
  active_days: number;
  actions: Record<string, number>;
}
interface RecordedTime {
  minutes: number;
  minutes_total: number;
  days: number;
  last: string | null;
}
interface OrgUser {
  user_id: number;
  name: string | null;
  email: string;
  is_staff: boolean;
  platform_role: string;
  org_role: string;
  joined_at: string | null;
  projects_created: number;
  last_project: string | null;
  pdfs_charged: number;
  last_upload: string | null;
  app: AppUse | null;
  time: RecordedTime | null;
  activity: Activity | null;
}
interface LedgerRow {
  id: number;
  type: string;
  source: string;
  amount: number;
  created_at: string | null;
  user_email: string | null;
  performed_by_email: string | null;
  description: string | null;
}
interface OrgDetail {
  users: OrgUser[];
  charged_non_members: Array<{ user_id: number; email: string | null; credits: number; last_upload: string | null }>;
  ledger: LedgerRow[];
  files: { uploads: number; files: number; pdfs: number };
  amplitude_available: boolean;
  recorded_since: string | null;
  error?: string;
}
interface CrmClient {
  key: string;
  name: string;
  stage: Stage;
  deal_name: string;
  deal_stage: string;
  amount: number | null;
  currency: string;
  close_date: string | null;
  overdue_days: number;
  open_value: number;
  contract_value_won: number;
  pilot_value_won: number;
  won_date: string | null;
  deals: Array<{ name: string; stage: string; amount: number | null; close_date: string | null }>;
}
interface OrgUsage {
  org_id: number;
  name: string;
  type: string | null;
  stage: Stage;
  crm: CrmClient | null;
  health: Health;
  days_idle: number | null;
  parent_group: string | null;
  earlier_product: boolean;
  merged_org_ids: number[];
  contract_since: string | null;
  project_count: number;
  upload_count: number;
  file_count: number;
  uploads_in: number;
  uploads_prev: number;
  files_in: number;
  projects_in: number;
  active_in_range: boolean;
  projects_in_progress: number;
  user_count: number;
  active_users_in: number | null;
  credits_total: number;
  credits_remaining: number;
  credit_pool: { left: number; size: number; since: string | null };
  credits: Credits;
  usage_check: UsageCheck;
  by_month: Record<string, MonthStat>;
  status_counts: Record<string, number>;
  first_activity: string | null;
  last_activity: string | null;
}
interface UsageResponse {
  orgs: OrgUsage[];
  crm_only?: CrmClient[];
  totals: {
    org_count?: number; user_count?: number; project_count?: number; file_count?: number;
    credits_consumed?: number;
    uploads_in?: number; uploads_prev?: number; projects_in?: number; projects_in_progress?: number;
    clients_active_in?: number; active_users_in?: number | null;
    stages?: Record<Stage, number>;
    health?: Record<Health, number>;
    contract_value_won?: number; customer_count?: number;
    pilot_value_won?: number; pilots_won?: number;
    open_value?: number; overdue_count?: number; overdue_value?: number;
  };
  ledger_available?: boolean;
  crm_available?: boolean;
  generated_at?: string;
  error?: string;
}

// Org logos. Clearbit's free logo API was shut down (every lookup 404'd, so
// the page only ever showed initials). We now use Google's favicon service,
// which needs a real domain — guessing one from the org name is unreliable
// for these German contractors (e.g. "SPIE Lück" → spie.de, not spie-lueck.de;
// "Elektro Beckhoff" → elektro-beckhoff.de, not beckhoff.de). So we keep a
// curated map of the known orgs, with a name-derived guess as a fallback.
// Match is by normalized-name substring (handles "Schlau GmbH Kalkulation",
// "ROM Technik (ZECH Group)", etc.).
const DOMAIN_MAP: Array<[string, string]> = [
  ['spie', 'spie.de'],
  ['rudolf fritz', 'rudolf-fritz.de'],
  ['elstek', 'elstek.de'],
  ['nws', 'elektronws.ch'],
  ['salvia', 'salvia-gebaeudetechnik.de'],
  ['rom technik', 'rom-technik.de'],
  ['bachner', 'bachner.de'],
  ['weisenburger', 'weisenburger.de'],
  ['brasst', 'brasst.de'],
  ['cms electric', 'cms-electric.de'],
  ['beckhoff', 'elektro-beckhoff.de'],
  ['schlau', 'schlau.de'],
  ['monco', 'monco.ai'],
];

function normalizeName(name: string): string {
  return (name || '')
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss');
}

// Candidate domains for an org, best first. Curated map wins; otherwise guess
// from the brand tokens (legal forms / generic trade words stripped).
function logoDomains(name: string): string[] {
  const norm = normalizeName(name);
  for (const [key, domain] of DOMAIN_MAP) {
    if (norm.includes(key)) return [domain];
  }
  const cleaned = norm
    .replace(/\b(gmbh|mbh|ag|kgaa|kg|co|se|ohg|gbr|ges|fuer|fur|und|group|gruppe|holding|bau|elektro|elektrotechnik|gebaeudetechnik|technik|inc|ltd|llc)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const tokens = cleaned.split(/\s+/).filter(Boolean);
  if (!tokens.length) return [];
  const hyphen = tokens.join('-');
  const joined = tokens.join('');
  const first = tokens[0];
  return [...new Set([`${hyphen}.de`, `${joined}.de`, `${first}.de`, `${hyphen}.com`, `${joined}.com`, `${first}.com`])];
}

function OrgLogo({ name }: { name: string }) {
  const urls = useMemo(
    () => logoDomains(name).map((d) => `https://www.google.com/s2/favicons?domain=${encodeURIComponent(d)}&sz=128`),
    [name],
  );
  const [idx, setIdx] = useState(0);
  const initials = (name || '?')
    .split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  if (idx >= urls.length) {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-slate-700 text-xs font-semibold text-slate-200">
        {initials}
      </div>
    );
  }
  return (
    <img
      src={urls[idx]}
      alt={name}
      onError={() => setIdx((i) => i + 1)}
      className="h-10 w-10 shrink-0 rounded-md bg-white object-contain p-1"
    />
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtUpdated(iso?: string): string {
  if (!iso) return '';
  const t = new Date(iso);
  return `Updated ${t.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

const STAGES: Array<{ key: Stage; label: string; blurb: string }> = [
  { key: 'customer', label: 'Customers', blurb: 'Yearly Smart Surveyor contract closed won.' },
  { key: 'contract_sent', label: 'Contract sent', blurb: 'Waiting for signature.' },
  { key: 'pilot', label: 'In pilot', blurb: 'Pilot running; yearly deal not signed yet.' },
  { key: 'proposal', label: 'Proposal sent', blurb: '' },
  { key: 'demo', label: 'Demo scheduled', blurb: '' },
  { key: 'qualified', label: 'Qualified', blurb: 'Leads in HubSpot.' },
  { key: 'no_deal', label: 'On the platform, no deal', blurb: 'Early pipeline — not in HubSpot yet.' },
  { key: 'lost', label: 'Lost', blurb: '' },
];

const HEALTH_STYLE: Record<Health, string> = {
  active: 'bg-emerald-500/15 text-emerald-300',
  slowing: 'bg-amber-500/15 text-amber-300',
  dormant: 'bg-slate-600/30 text-slate-400',
};

const eur = (v: number | null | undefined) =>
  v == null ? '—' : `€${Math.round(v).toLocaleString('de-DE')}`;

function idleLabel(o: OrgUsage): string {
  if (o.days_idle == null) return 'never active';
  if (o.days_idle === 0) return 'active today';
  return `${o.health} · ${o.days_idle}d ago`;
}

// Uploads per month for the last 12 months; months under contract are green.
function MonthlyBars({ byMonth, contractSince }: { byMonth: Record<string, MonthStat>; contractSince: string | null }) {
  const now = new Date();
  const months: string[] = [];
  for (let i = 11; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  const contractMonth = contractSince ? contractSince.slice(0, 7) : null;
  const max = Math.max(1, ...months.map((m) => byMonth[m]?.uploads ?? 0));
  return (
    <div className="flex items-end gap-[3px]" style={{ height: 36 }}>
      {months.map((m) => {
        const s = byMonth[m];
        const v = s?.uploads ?? 0;
        const h = v ? Math.round((v / max) * 32) + 3 : 1;
        const underContract = contractMonth != null && m >= contractMonth;
        return (
          <div
            key={m}
            title={`${m}: ${v} uploads, ${s?.files ?? 0} files, ${s?.projects ?? 0} new projects`}
            className={`w-2 rounded-sm ${v ? (underContract ? 'bg-emerald-400' : 'bg-indigo-400') : 'bg-slate-700'}`}
            style={{ height: h }}
          />
        );
      })}
    </div>
  );
}

function Trend({ now, prev }: { now: number; prev: number }) {
  if (now === prev) return <span className="text-slate-500">±0</span>;
  const up = now > prev;
  return <span className={up ? 'text-emerald-400' : 'text-rose-400'}>{up ? '▲' : '▼'} {Math.abs(now - prev)}</span>;
}

function Stat({ value, label, sub }: { value: React.ReactNode; label: string; sub?: React.ReactNode }) {
  return (
    <div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-slate-400">{label}</div>
      {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
    </div>
  );
}

// The HubSpot deal behind a client: stage, value, and whether it is late.
function DealBadge({ c }: { c: CrmClient }) {
  const tip = c.deals.map((d) => `${d.name}: ${d.stage}${d.amount != null ? ` · ${eur(d.amount)}` : ''}`).join('\n');
  const won = c.contract_value_won > 0;
  return (
    <span className="flex flex-wrap items-center gap-1.5 text-xs" title={tip}>
      <span className={`rounded px-1.5 py-0.5 text-[10px] ${won ? 'bg-emerald-500/15 text-emerald-300' : 'bg-indigo-500/15 text-indigo-300'}`}>
        {c.deal_stage}
      </span>
      <span className="text-slate-300">{eur(won ? c.contract_value_won : c.amount)}{won ? ' / yr' : ''}</span>
      {c.pilot_value_won > 0 && !won && <span className="text-slate-500">· pilot paid</span>}
      {won && c.won_date && <span className="text-slate-500">· since {fmtDate(c.won_date)}</span>}
      {c.overdue_days > 0 && (
        <span className="rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] text-rose-300">
          close date passed {c.overdue_days}d ago
        </span>
      )}
    </span>
  );
}

function creditLine(o: OrgUsage): string {
  const c = o.credits;
  const n = (v: number) => v.toLocaleString();
  if (!o.credits_total && !c.consumed) return 'No credits';
  const parts = [`${n(c.consumed)} credits used`];
  if (o.credit_pool.size) {
    parts.push(`${n(o.credit_pool.left)} of ${n(o.credit_pool.size)} left${o.credit_pool.since ? ` since ${fmtDate(o.credit_pool.since)}` : ''}`);
  }
  if (o.contract_since) parts.push(`reset ${fmtDate(o.contract_since)} (${n(c.pilot_used)} before, ${n(c.contract_used)} after)`);
  return parts.join(' · ');
}

function ClientRow({ o, open, onToggle, range }: { o: OrgUsage; open: boolean; onToggle: () => void; range: RangeInfo }) {
  const c = o.credits;
  const lowCredits = o.credit_pool.size > 0 && o.credit_pool.left / o.credit_pool.size < 0.15;
  const creditTitle = [
    `Credits used = upload charges (1 per PDF), lifetime: ${c.consumed}. Added: ${c.purchased}.`,
    c.adjusted_manual ? `Removed by hand (balance adjustments, not usage): ${c.adjusted_manual}` : null,
    c.resets.length ? `Reset ${c.resets.length}×, ${c.reset_removed} credits removed` : null,
    `Balance: ${o.credits_remaining} left of ${o.credits_total} ever granted (incl. credits later removed by reset/hand)`,
  ].filter(Boolean).join('\n');
  return (
    <div className={`rounded-lg border bg-slate-900/40 ${open ? 'border-indigo-500/50' : 'border-slate-800'}`}>
    <div
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={onToggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      className="flex cursor-pointer items-center gap-4 rounded-lg px-5 py-3 hover:bg-slate-800/30"
    >
      <span className={`text-slate-500 transition-transform ${open ? 'rotate-90' : ''}`}>›</span>
      <OrgLogo name={o.name} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate font-medium" title={o.name}>{o.name}</span>
          <span className={`rounded px-1.5 py-0.5 text-[10px] ${HEALTH_STYLE[o.health]}`}>{idleLabel(o)}</span>
          {o.parent_group && <span className="rounded bg-slate-700/50 px-1.5 py-0.5 text-[10px] text-slate-300">{o.parent_group}</span>}
          {o.earlier_product && (
            <span className="rounded bg-slate-700/50 px-1.5 py-0.5 text-[10px] text-slate-300" title={`Includes org #${o.merged_org_ids.join(', #')} from the earlier product`}>
              earlier product
            </span>
          )}
          {lowCredits && <span className="rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] text-rose-300">low credits</span>}
          {!o.usage_check.ok && (
            <span className="rounded bg-orange-500/15 px-1.5 py-0.5 text-[10px] text-orange-300" title={o.usage_check.issues.join('\n')}>
              check usage
            </span>
          )}
        </div>
        {o.crm && <div className="mt-1"><DealBadge c={o.crm} /></div>}
        <div className="mt-0.5 truncate text-[11px] text-slate-500" title={creditTitle}>
          {creditLine(o)} · client since {fmtDate(o.first_activity)}
        </div>
      </div>
      <div className="hidden md:block"><MonthlyBars byMonth={o.by_month} contractSince={o.crm?.won_date ?? o.contract_since} /></div>
      <div className="w-16 text-right" title={`Members who used the app (${range.label.toLowerCase()}) / all members`}>
        <div className="text-lg font-semibold">
          {o.active_users_in != null ? <>{o.active_users_in}<span className="text-sm text-slate-500">/{o.user_count}</span></> : o.user_count}
        </div>
        <div className="text-[10px] uppercase text-slate-500">{o.active_users_in != null ? 'Active users' : 'Users'}</div>
      </div>
      <div className="w-20 text-right" title={`${o.uploads_in} uploads (${range.label.toLowerCase()})${range.hasPrev ? ` vs ${o.uploads_prev} in the period before` : ''}`}>
        <div className="text-lg font-semibold">
          {o.uploads_in}{range.hasPrev && <> <span className="text-[10px] font-normal"><Trend now={o.uploads_in} prev={o.uploads_prev} /></span></>}
        </div>
        <div className="text-[10px] uppercase text-slate-500">Uploads · {range.short}</div>
      </div>
      <div className="w-20 text-right" title={Object.entries(o.status_counts).map(([s, n]) => `${s}: ${n}`).join('\n')}>
        <div className="text-lg font-semibold">{o.project_count}</div>
        <div className="text-[10px] uppercase text-slate-500">
          Projects{o.projects_in_progress ? <span className="text-amber-300"> · {o.projects_in_progress} open</span> : null}
        </div>
      </div>
      <div className="w-20 text-right" title={creditTitle}>
        <div className="text-lg font-semibold text-emerald-400">{c.consumed.toLocaleString()}</div>
        <div className="text-[10px] uppercase text-slate-500">Credits used</div>
      </div>
    </div>
    {open && <ClientDetail o={o} range={range} />}
    </div>
  );
}

function fmtMinutes(m: number | null | undefined): string {
  if (m == null) return '—';
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}`;
}

function ago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  return `${d}d ago`;
}

function latest(...isos: Array<string | null | undefined>): string | null {
  return isos.filter(Boolean).sort().pop() ?? null;
}

const ACTION_LABEL: Record<string, string> = {
  upload: 'uploads', project: 'projects', review: 'takeoff reviews', agent_chat: 'agent chats', comparison: 'plan comparisons',
};

// Time in app: Amplitude when connected, else what we have recorded.
function appMinutes30(u: OrgUser, amplitude: boolean): number | null {
  if (amplitude && u.activity) return u.activity.active_minutes;
  return u.time ? u.time.minutes : null;
}

interface LedgerLine { key: string; date: string | null; credits: string; tone: string; label: string; detail: string | null; who: string }

// Plain-language credit history: same-day uploads by one person become one line.
function ledgerView(rows: LedgerRow[]): LedgerLine[] {
  const out: LedgerLine[] = [];
  let group: { rows: LedgerRow[]; day: string; who: string } | null = null;
  const flush = () => {
    if (!group) return;
    const n = group.rows.reduce((a, r) => a + r.amount, 0);
    out.push({
      key: `u${group.rows[0].id}`,
      date: group.rows[0].created_at,
      credits: `−${n}`,
      tone: 'text-rose-300',
      label: `Upload: ${n} PDF${n === 1 ? '' : 's'} charged`,
      detail: group.rows.length > 1 ? `${group.rows.length} uploads that day` : group.rows[0].description,
      who: group.who,
    });
    group = null;
  };
  for (const r of rows) {
    const day = (r.created_at || '').slice(0, 10);
    const who = r.user_email || '—';
    if (r.type === 'deduction' && r.source === 'system_upload') {
      if (group && group.day === day && group.who === who) { group.rows.push(r); continue; }
      flush();
      group = { rows: [r], day, who };
      continue;
    }
    flush();
    const by = r.performed_by_email && r.performed_by_email !== r.user_email ? r.performed_by_email : r.user_email || '—';
    if (r.type === 'addition') {
      out.push({ key: `a${r.id}`, date: r.created_at, credits: `+${r.amount}`, tone: 'text-emerald-300',
        label: 'Credits added', detail: r.description, who: `by ${by}` });
    } else if (r.type === 'reset') {
      out.push({ key: `r${r.id}`, date: r.created_at, credits: `−${r.amount}`, tone: 'text-amber-300',
        label: 'Reset to contract: unused pilot credits removed', detail: r.description, who: `by ${by}` });
    } else {
      out.push({ key: `d${r.id}`, date: r.created_at, credits: `−${r.amount}`, tone: 'text-rose-300',
        label: 'Deducted by hand', detail: r.description, who: `by ${by}` });
    }
  }
  flush();
  return out;
}

const TH = 'px-2 py-1.5 text-left text-[10px] font-medium uppercase tracking-wide text-slate-500';
const TD = 'px-2 py-1.5 align-top';

// The drill-down under a client row: who uses it, how much, and the credit audit.
function ClientDetail({ o, range }: { o: OrgUsage; range: RangeInfo }) {
  const [detail, setDetail] = useState<OrgDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showLedger, setShowLedger] = useState(false);

  useEffect(() => {
    let cancel = false;
    setDetail(null);
    fetch(`/api/usage/org/${o.org_id}?${range.qs}`, { credentials: 'same-origin' })
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (cancel) return;
        if (!r.ok || body.error) setError(body.error || (body.locked ? 'locked — reload the page' : `HTTP ${r.status}`));
        else setDetail(body as OrgDetail);
      })
      .catch((e) => { if (!cancel) setError((e as Error).message); });
    return () => { cancel = true; };
  }, [o.org_id, range.qs]);

  if (error) return <div className="border-t border-slate-800 px-5 py-3 text-sm text-red-400">Failed to load: {error}</div>;
  if (!detail) return <div className="border-t border-slate-800 px-5 py-3 text-sm text-slate-400">Loading users…</div>;

  const uc = o.usage_check;
  const amp = detail.amplitude_available;
  const activeUsers = detail.users.filter((u) => (u.app?.active_days ?? 0) > 0 || (u.time?.minutes ?? 0) > 0).length;
  const minutes30 = detail.users.reduce((n, u) => n + (appMinutes30(u, amp) ?? 0), 0);
  const staff = detail.users.filter((u) => u.is_staff).length;
  const timeSource = amp ? 'Amplitude' : detail.recorded_since ? `recorded since ${fmtDate(detail.recorded_since)}` : 'recording starts after deploy';
  const ledgerLines = ledgerView(detail.ledger);
  const shownLines = showLedger ? ledgerLines : ledgerLines.slice(0, 8);

  return (
    <div className="border-t border-slate-800 px-5 py-4 text-sm" onClick={(e) => e.stopPropagation()}>
      <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-5">
        <Stat value={detail.users.length} label="Users" sub={staff ? `${staff} Monco staff` : 'no Monco staff'} />
        <Stat
          value={<>{activeUsers}<span className="text-base text-slate-500"> / {detail.users.length}</span></>}
          label={`Used the app · ${range.short}`}
          sub={`${fmtMinutes(minutes30)} in app (${timeSource})`}
        />
        <Stat value={detail.files.pdfs} label="PDFs uploaded" sub={`in ${detail.files.uploads} uploads`} />
        <Stat
          value={uc.credits_upload}
          label="Credits used (uploads)"
          sub={`${o.credits.adjusted_manual} removed by hand · ${o.credits.consumed_by_staff} charged to staff`}
        />
        <Stat
          value={uc.ok ? <span className="text-emerald-400">OK</span> : <span className="text-orange-300">Check</span>}
          label="Usage check"
          sub={uc.ok ? 'credits match uploads' : `${uc.issues.length} issue${uc.issues.length > 1 ? 's' : ''}`}
        />
      </div>
      {!uc.ok && (
        <ul className="mb-4 list-disc rounded border border-orange-500/30 bg-orange-500/10 py-2 pl-8 pr-3 text-xs text-orange-200">
          {uc.issues.map((i) => <li key={i}>{i}</li>)}
        </ul>
      )}
      {uc.notes.length > 0 && (
        <p className="mb-4 text-xs text-slate-400">{uc.notes.join(' · ')}</p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] text-xs">
          <thead>
            <tr className="border-b border-slate-800">
              <th className={TH}>User</th>
              <th className={TH}>Joined</th>
              <th className={`${TH} text-right`}>Projects</th>
              <th className={`${TH} text-right`}>PDFs charged</th>
              <th className={TH}>Last upload</th>
              <th className={`${TH} text-right`}>Days active · {range.short}</th>
              <th className={`${TH} text-right`}>Actions · {range.short}</th>
              <th className={`${TH} text-right`}>Time in app · {range.short}</th>
              <th className={TH}>Last seen</th>
            </tr>
          </thead>
          <tbody>
            {detail.users.map((u) => (
              <tr key={u.user_id} className="border-b border-slate-800/60">
                <td className={TD}>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-slate-200">{u.name || u.email}</span>
                    {u.is_staff && <span className="rounded bg-indigo-500/20 px-1 text-[10px] text-indigo-300">Monco staff</span>}
                    {u.org_role && u.org_role !== 'member' && <span className="rounded bg-slate-700/60 px-1 text-[10px] text-slate-300">{u.org_role}</span>}
                    {u.platform_role && !['user', 'quantity_surveyor'].includes(u.platform_role) && (
                      <span className="rounded bg-slate-700/60 px-1 text-[10px] text-slate-400">{u.platform_role}</span>
                    )}
                  </div>
                  {u.name && <div className="text-[11px] text-slate-500">{u.email}</div>}
                </td>
                <td className={`${TD} text-slate-400`}>{fmtDate(u.joined_at)}</td>
                <td className={`${TD} text-right`}>{u.projects_created}</td>
                <td className={`${TD} text-right`}>{u.pdfs_charged}</td>
                <td className={`${TD} text-slate-400`}>{ago(u.last_upload)}</td>
                <td className={`${TD} text-right`}>{u.app ? u.app.active_days : '—'}</td>
                <td
                  className={`${TD} text-right`}
                  title={u.app ? Object.entries(u.app.actions).map(([k, n]) => `${n} ${ACTION_LABEL[k] ?? k}`).join('\n') || 'no actions' : ''}
                >
                  {u.app ? Object.values(u.app.actions).reduce((a, b) => a + b, 0) : '—'}
                </td>
                <td className={`${TD} text-right`}>{fmtMinutes(appMinutes30(u, amp))}</td>
                <td className={`${TD} text-slate-400`}>{ago(latest(u.app?.last_seen, u.app?.last_action, u.time?.last, u.activity?.last_seen))}</td>
              </tr>
            ))}
            {detail.charged_non_members.map((c) => (
              <tr key={`x${c.user_id}`} className="border-b border-slate-800/60 text-orange-200/80">
                <td className={TD} colSpan={3}>{c.email || `user #${c.user_id}`} <span className="text-[10px]">(charged here, not a member)</span></td>
                <td className={`${TD} text-right`}>{c.credits}</td>
                <td className={TD} colSpan={5}>{ago(c.last_upload)}</td>
              </tr>
            ))}
            {detail.users.length === 0 && (
              <tr><td className={`${TD} text-slate-500`} colSpan={9}>No users in this organization.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-1 text-[10px] text-slate-600">
        PDFs charged = upload credits recorded against the user (1 per PDF; Monco super-admin uploads are free).
        Days active / actions = days with an upload, new project, takeoff review, agent chat or plan comparison.
        Time in app ≈ 15 min per session renewal (the app renews a signed-in user&apos;s session when they return
        after 15 min), recorded by the agent from deploy onwards{amp ? '; Amplitude session time where connected' : ''}.
      </p>

      {ledgerLines.length > 0 && (
        <div className="mt-5">
          <div className="mb-1 text-xs font-medium text-slate-400">
            Credit history <span className="font-normal text-slate-600">— every change to this client&apos;s credits, newest first</span>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-800">
                <th className={TH}>Date</th>
                <th className={`${TH} text-right`}>Credits</th>
                <th className={TH}>What happened</th>
                <th className={TH}>Who</th>
              </tr>
            </thead>
            <tbody>
              {shownLines.map((l) => (
                <tr key={l.key} className="border-b border-slate-800/60">
                  <td className={`${TD} whitespace-nowrap text-slate-400`}>{fmtDate(l.date)}</td>
                  <td className={`${TD} whitespace-nowrap text-right font-medium ${l.tone}`}>{l.credits}</td>
                  <td className={TD}>
                    <span className="text-slate-200">{l.label}</span>
                    {l.detail && <div className="text-[11px] text-slate-500">{l.detail}</div>}
                  </td>
                  <td className={`${TD} text-slate-400`}>{l.who}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {ledgerLines.length > 8 && (
            <button onClick={() => setShowLedger((v) => !v)} className="mt-1 text-xs text-indigo-400 hover:underline">
              {showLedger ? 'Show less' : `Show all ${ledgerLines.length}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// A HubSpot client with no org on the platform yet.
function CrmOnlyRow({ c }: { c: CrmClient }) {
  return (
    <div className="flex items-center gap-4 rounded-lg border border-dashed border-slate-800 px-5 py-2.5">
      <OrgLogo name={c.name} />
      <div className="min-w-0 flex-1">
        <div className="font-medium">{c.name}</div>
        <div className="mt-1"><DealBadge c={c} /></div>
      </div>
      <span className="text-xs text-slate-500">not on the platform</span>
    </div>
  );
}

type Load =
  | { state: 'loading' }
  | { state: 'locked'; configured: boolean }
  | { state: 'error'; message: string }
  | { state: 'ready'; data: UsageResponse };

async function loadUsage(qs: string, refresh = false): Promise<Load> {
  try {
    const res = await fetch(`/api/usage?${qs}${refresh ? '&refresh=1' : ''}`, { credentials: 'same-origin' });
    const body = await res.json().catch(() => ({}));
    if (res.status === 401 && body.locked) return { state: 'locked', configured: body.configured !== false };
    if (!res.ok || body.error) return { state: 'error', message: body.error || `HTTP ${res.status}` };
    return { state: 'ready', data: body as UsageResponse };
  } catch (e) {
    return { state: 'error', message: (e as Error).message };
  }
}

function UnlockForm({ configured, onUnlocked }: { configured: boolean; onUnlocked: () => void }) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/usage/unlock', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) onUnlocked();
      else setError(body.error || `HTTP ${res.status}`);
    } finally {
      setBusy(false);
      setPassword('');
    }
  };
  return (
    <div className="mx-auto mt-24 max-w-sm rounded-lg border border-slate-800 bg-slate-900/40 p-6">
      <h2 className="mb-1 text-lg font-semibold">Founders only</h2>
      {configured ? (
        <form onSubmit={submit}>
          <p className="mb-4 text-sm text-slate-400">Enter the founders password to see the client overview.</p>
          <div className="relative mb-3">
            {/* This app has no Tailwind preflight, so inputs keep the browser's dark
                text colour unless it is set — which was invisible on this background. */}
            <input
              type={showPassword ? 'text' : 'password'}
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-slate-700 bg-slate-950 py-2 pl-3 pr-16 text-sm text-slate-100 caret-slate-100 outline-none [color-scheme:dark] placeholder:text-slate-500 focus:border-indigo-500"
              placeholder="Password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute inset-y-0 right-2 my-auto h-7 rounded border-0 bg-transparent px-2 text-xs text-slate-400 hover:bg-slate-800 hover:text-slate-200"
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          {error && <p className="mb-3 text-xs text-rose-400">{error}</p>}
          <button
            type="submit"
            disabled={busy || !password}
            className="w-full rounded-md bg-indigo-500 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-400 disabled:opacity-50"
          >
            {busy ? 'Checking…' : 'Unlock'}
          </button>
        </form>
      ) : (
        <p className="text-sm text-slate-400">No founders password is set yet (run scripts/set-founders-password.sh), so this page stays locked.</p>
      )}
    </div>
  );
}

// ── Date range ────────────────────────────────────────────
type Preset = '7d' | '30d' | '90d' | 'month' | 'last_month' | 'year' | 'all' | 'custom';
const PRESETS: Array<{ key: Preset; label: string }> = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
  { key: 'month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'year', label: 'This year' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom' },
];

interface RangeInfo {
  qs: string;        // query string for the API
  label: string;     // "Last 30 days", "1 Sept – 25 Sept 2026"
  short: string;     // column-header form: "30d", "Sep", "1 Sept–15 Sept"
  hasPrev: boolean;  // trend vs the equally long period before
}

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const dayLabel = (s: string) =>
  new Date(`${s}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

function rangeInfo(preset: Preset, from: string, to: string): RangeInfo {
  const today = new Date();
  const back = (n: number) => { const d = new Date(today); d.setDate(d.getDate() - (n - 1)); return ymd(d); };
  switch (preset) {
    case '7d': case '30d': case '90d': {
      const n = parseInt(preset, 10);
      return { qs: `from=${back(n)}&to=${ymd(today)}`, label: `Last ${n} days`, short: `${n}d`, hasPrev: true };
    }
    case 'month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { qs: `from=${ymd(start)}&to=${ymd(today)}`, label: 'This month', short: 'this month', hasPrev: true };
    }
    case 'last_month': {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      const name = start.toLocaleDateString('en-GB', { month: 'short' });
      return { qs: `from=${ymd(start)}&to=${ymd(end)}`, label: `Last month (${name})`, short: name, hasPrev: true };
    }
    case 'year': {
      const start = new Date(today.getFullYear(), 0, 1);
      return { qs: `from=${ymd(start)}&to=${ymd(today)}`, label: `This year (${today.getFullYear()})`, short: String(today.getFullYear()), hasPrev: true };
    }
    case 'all':
      return { qs: 'from=all', label: 'All time', short: 'all time', hasPrev: false };
    default: {
      const f = from || back(30);
      const t = to && to >= f ? to : ymd(today);
      const d = (x: string) => new Date(`${x}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      return { qs: `from=${f}&to=${t}`, label: `${dayLabel(f)} – ${dayLabel(t)}`, short: `${d(f)}–${d(t)}`, hasPrev: true };
    }
  }
}

function RangePicker({ preset, from, to, onChange }: {
  preset: Preset; from: string; to: string; onChange: (p: Preset, from: string, to: string) => void;
}) {
  const today = ymd(new Date());
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex flex-wrap rounded-md border border-slate-700 p-0.5">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => onChange(p.key, from, to)}
            className={`rounded px-2.5 py-1 text-xs ${preset === p.key ? 'bg-indigo-500 text-white' : 'text-slate-400 hover:text-slate-200'}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {preset === 'custom' && (
        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          <input
            type="date" value={from} max={to || today}
            onChange={(e) => onChange('custom', e.target.value, to)}
            className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-slate-200 [color-scheme:dark]"
          />
          <span>to</span>
          <input
            type="date" value={to} min={from} max={today}
            onChange={(e) => onChange('custom', from, e.target.value)}
            className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-slate-200 [color-scheme:dark]"
          />
        </div>
      )}
    </div>
  );
}

// Remember the last range per viewer (a convenience; the page works without it).
const RANGE_KEY = 'usage.range';
function savedRange(): { preset: Preset; from: string; to: string } {
  try {
    const v = JSON.parse(localStorage.getItem(RANGE_KEY) || 'null');
    if (v && PRESETS.some((p) => p.key === v.preset)) return { preset: v.preset, from: v.from || '', to: v.to || '' };
  } catch { /* storage unavailable */ }
  return { preset: '30d', from: '', to: '' };
}

export function UsagePage(): JSX.Element {
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [refreshing, setRefreshing] = useState(false);
  const [openOrg, setOpenOrg] = useState<number | null>(null);
  const [sel, setSel] = useState(savedRange);
  const range = useMemo(() => rangeInfo(sel.preset, sel.from, sel.to), [sel]);

  const changeRange = (preset: Preset, from: string, to: string) => {
    const next = { preset, from, to };
    setSel(next);
    try { localStorage.setItem(RANGE_KEY, JSON.stringify(next)); } catch { /* storage unavailable */ }
  };

  useEffect(() => {
    let cancel = false;
    loadUsage(range.qs).then((l) => { if (!cancel) setLoad((cur) => (cur.state === 'locked' && l.state !== 'ready' ? cur : l)); });
    return () => { cancel = true; };
  }, [range.qs]);

  const refresh = () => {
    setRefreshing(true);
    loadUsage(range.qs, true).then(setLoad).finally(() => setRefreshing(false));
  };
  const lock = () => {
    fetch('/api/usage/lock', { method: 'POST', credentials: 'same-origin' })
      .finally(() => setLoad({ state: 'locked', configured: true }));
  };

  const data = load.state === 'ready' ? load.data : null;
  const t = data?.totals ?? {};
  const stageCounts = t.stages;
  const health = t.health ?? { active: 0, slowing: 0, dormant: 0 };

  return (
    <div className="h-screen overflow-y-auto bg-[#0a0e17] px-6 py-8 text-slate-200">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Client Overview</h1>
            <p className="text-sm text-slate-400">Where each client stands: HubSpot stage, platform activity and credits</p>
          </div>
          <div className="flex items-center gap-4">
            {data && <span className="text-xs text-slate-500">{fmtUpdated(data.generated_at)}</span>}
            {data && (
              <>
                <button
                  onClick={refresh}
                  disabled={refreshing}
                  className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                >
                  {refreshing ? 'Refreshing…' : 'Refresh'}
                </button>
                <button onClick={lock} className="text-sm text-slate-400 hover:text-slate-200">Lock</button>
              </>
            )}
            <Link to="/" className="text-sm text-indigo-400 hover:underline">&larr; Dashboard</Link>
          </div>
        </div>

        {load.state !== 'locked' && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <RangePicker preset={sel.preset} from={sel.from} to={sel.to} onChange={changeRange} />
            <span className="text-xs text-slate-500">
              Activity and uploads: <span className="text-slate-300">{range.label}</span> · credits are always all time
              {range.hasPrev && ' · trends vs the period before'}
            </span>
          </div>
        )}

        {load.state === 'loading' && <div className="text-slate-400">Loading clients…</div>}
        {load.state === 'error' && <div className="text-red-400">Failed to load: {load.message}</div>}
        {load.state === 'locked' && (
          <UnlockForm
            configured={load.configured}
            onUnlocked={() => { setLoad({ state: 'loading' }); loadUsage(range.qs).then(setLoad); }}
          />
        )}

        {data && (
          <>
            {data.crm_available === false && (
              <p className="mb-4 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                HubSpot could not be read (SSM /monco/agent/hubspot-token missing or invalid), so no client has a deal stage below.
              </p>
            )}
            {data.ledger_available === false && (
              <p className="mb-4 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                The Monco DB could not be read, so credits and users below are incomplete.
              </p>
            )}

            <div className="mb-6 grid grid-cols-2 gap-6 rounded-lg border border-slate-800 bg-slate-900/40 px-6 py-4 md:grid-cols-5">
              <Stat
                value={<span className="text-emerald-400">{eur(t.contract_value_won ?? 0)}</span>}
                label="Signed yearly contracts"
                sub={`${t.customer_count ?? 0} customers · ${eur(t.pilot_value_won ?? 0)} from ${t.pilots_won ?? 0} pilots`}
              />
              <Stat
                value={eur(t.open_value ?? 0)}
                label="Open pipeline"
                sub={t.overdue_count
                  ? <span className="text-rose-300">{t.overdue_count} past close date · {eur(t.overdue_value ?? 0)}</span>
                  : 'nothing overdue'}
              />
              <Stat
                value={<span className="text-emerald-400">{t.clients_active_in ?? 0}<span className="text-base text-slate-500"> / {t.org_count ?? 0}</span></span>}
                label={`Clients active · ${range.short}`}
                sub={`${t.active_users_in ?? '—'} users · now: ${health.active} active, ${health.slowing} slowing, ${health.dormant} dormant`}
              />
              <Stat
                value={t.uploads_in ?? 0}
                label={`Uploads · ${range.short}`}
                sub={range.hasPrev
                  ? <><Trend now={t.uploads_in ?? 0} prev={t.uploads_prev ?? 0} /> vs period before · {t.projects_in ?? 0} new projects</>
                  : `${t.projects_in ?? 0} projects`}
              />
              <Stat
                value={(t.credits_consumed ?? 0).toLocaleString()}
                label="Credits used (all time)"
                sub={`upload charges, 1 per PDF · ${t.projects_in_progress ?? 0} projects open`}
              />
            </div>

            {STAGES.map((s) => {
              const rows = data.orgs.filter((o) => o.stage === s.key);
              const crmRows = (data.crm_only ?? []).filter((c) => c.stage === s.key);
              if (!rows.length && !crmRows.length) return null;
              return (
                <section key={s.key} className="mb-8">
                  <div className="mb-2 flex items-baseline gap-3">
                    <h2 className="text-lg font-semibold">
                      {s.label} <span className="text-slate-500">· {stageCounts?.[s.key] ?? rows.length + crmRows.length}</span>
                    </h2>
                    {s.blurb && <span className="text-xs text-slate-500">{s.blurb}</span>}
                  </div>
                  <div className="space-y-2">
                    {rows.map((o) => (
                      <ClientRow
                        key={o.org_id}
                        o={o}
                        range={range}
                        open={openOrg === o.org_id}
                        onToggle={() => setOpenOrg((cur) => (cur === o.org_id ? null : o.org_id))}
                      />
                    ))}
                    {crmRows.map((c) => <CrmOnlyRow key={c.key} c={c} />)}
                  </div>
                </section>
              );
            })}
            {data.orgs.length === 0 && <div className="text-slate-400">No organizations found.</div>}

            <p className="mt-6 text-xs text-slate-600">
              Stage and deal values come from HubSpot (a client&apos;s yearly deal, or its pilot deal if there is no yearly one yet).
              Deals are matched to platform orgs by name. Activity, users and credits come from the platform. Credits used = upload
              charges (1 credit per PDF a client uploads); credits removed by hand are balance adjustments and are not counted.
              The date range applies to activity and uploads only; credits, stage and balances are always all time / current.
              Within a stage, clients are ordered by uploads in the range. Internal/test orgs are excluded; orgs from the earlier
              product are merged into the client&apos;s current org.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
